// src/flows/masterclass/ai/StepAI_Objectives.tsx
import { Box, TextField, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { AIStepLayout } from './AIStepLayout';
import { useProgramCreation } from '../../ProgramCreationContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import DosenInput from '../../../../../pages/new-designs/components/DosenInput';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';

export function StepAI_Objectives() {
  const { state, dispatch } = useProgramCreation();
  const navigate = useNavigate();

  const [objectives, setObjectives] = useState([...state.ai.objectives]);
  const [touchedObjectives, setTouchedObjectives] = useState<boolean[]>([...state.ai.objectives].map(() => false));

  const minObjectiveLength = 20;
  const objectivesValid = objectives.every((o) => o.trim().length >= minObjectiveLength);
  const canDelete = objectives.length > 5;
  const canAdd = objectivesValid;

  useEffect(() => {
    setTouchedObjectives((prev) => {
      const clone = [...prev];
      while (clone.length < objectives.length) {
        clone.push(false);
      }
      while (clone.length > objectives.length) {
        clone.pop();
      }
      return clone;
    });
  }, [objectives.length]);

  const handleNext = () => {
    dispatch({ type: 'UPDATE_AI_OBJECTIVES', payload: objectives });
    dispatch({ type: 'SET_AI_STEP', payload: 3 });
    navigate('../documents');
  };

  return (
    <AIStepLayout
      stepTitle="Learning objectives"
      title="Learning objectives"
      onBack={() => navigate(-1)}
      onNext={handleNext}
      nextDisabled={!objectivesValid}
      nextLabel="Save"
    >
      <Box
        sx={{
          width: '100%',
          color: 'var(--Text-text-secondary, #B6B6B6)',
          fontSize: 14,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          lineHeight: '19.88px',
          wordWrap: 'break-word',
          mt: '-30px'
        }}
      >
        What specific objectives should participants achieve by the end of this Course? For example: ‘Understanding
        Our Company Mission’, ‘Learning Our Hiring Process’, or ‘Recognizing Our Organization’s Values’.
      </Box>
      <Box display="flex" flexDirection="column" gap={2} sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }}>
        {objectives.map((obj, i) => {
          const trimmed = obj.trim();
          const showError = touchedObjectives[i] && trimmed.length < minObjectiveLength;
          return (
            <Box key={i} display="flex" gap={1}>
              <DosenInput
                fullWidth
                label={`Objective ${i + 1}`}
                value={obj}
                error={showError}
                helperText={showError ? 'Each objective requires at least 20 characters.' : ''}
                onChange={(e) => setObjectives((prev) => prev.map((o, idx) => (idx === i ? e.target.value : o)))}
                onBlur={() => setTouchedObjectives((prev) => prev.map((bool, idx) => (idx === i ? true : bool)))}
              />
              {canDelete && (
                <IconButton onClick={() => setObjectives(objectives.filter((_, idx) => idx !== i))}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          );
        })}

        {canAdd && (
          <Box display="flex">
            <DosenButton
              iconOnly
              tone="basic"
              onClick={() => setObjectives([...objectives, ''])}
              aria-label="Add objective"
            >
              <AddIcon sx={{ fontSize: 14, color: '#FDFDFD' }} />
            </DosenButton>
          </Box>
        )}
      </Box>
    </AIStepLayout>
  );
}
